import { User, UserCreditInfo } from '@repo/domain/user';
import env from '@repo/env';
import {
  useOnboardingStore,
  BASE_ONBOARDING,
} from '@repo/onboarding/onboarding-store';
import {
  getProfileToken,
  setUserToken,
  setProfileToken,
} from '@repo/transport/token';
import { buildProfileImageUrl } from '@repo/utils/url';
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';

import userApi from '../../api/user';
import { DEVICE_ID_KEY, ONBOARDING_KEY } from '../../global/constants';
import { ResponseWithError, UserResponse } from '../../types';

const COOKIE_PARAMS: Cookies.CookieAttributes = {
  expires: 30,
  path: '/',
  domain: env.cookieDomain,
  sameSite: 'lax',
  secure: env.nodeEnv !== 'development',
};

// get deviceId from cookie (or set it if it's not there)
export const getDeviceId = (): string => {
  let deviceId = Cookies.get(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = uuidv4();
    Cookies.set(DEVICE_ID_KEY, deviceId!, COOKIE_PARAMS);
  }
  return deviceId!;
};

// utility to get information about the current user
export const getUserInfo = async (
  accessToken?: string
): Promise<User | null> => {
  const _accessToken = accessToken || getProfileToken();

  if (_accessToken) {
    const response = await userApi.getUserInfo(_accessToken);

    // clear tokens as they are no longer valid
    if ('error' in response && response.error === 'UNAUTHORIZED') {
      setProfileToken(null);
      setUserToken(null);
      return null;
    }
    if ((response as ResponseWithError).error) {
      return null;
    }
    const res = response as UserResponse;
    const profile = res.profile ?? res;
    // actual user
    const user = res.user ?? { ...profile };
    // current profile being used
    if (user && user.profileImage) {
      user.profileImage = buildProfileImageUrl(user.profileImage);
    }
    if (profile && profile.profileImage) {
      profile.profileImage = buildProfileImageUrl(profile.profileImage);
    }

    return {
      ...profile,
      user: user,
      profile: profile,
      team: res.team,
      isTeam: !!res.team,
    };
  }

  return null;
};

// utility to get information about the current user credit info
export const getUserCreditInfo = async (
  accessToken?: string
): Promise<UserCreditInfo | null> => {
  const _accessToken = accessToken || getProfileToken();

  if (_accessToken) {
    const response = await userApi.getUserCreditInfo(_accessToken);

    if (response && 'data' in response) {
      return response.data;
    }
  }

  return null;
};

export const updateOnboarding = async (user: User): Promise<void> => {
  const { init } = useOnboardingStore.getState();
  const localOnboarding = JSON.parse(sessionStorage.getItem(ONBOARDING_KEY)!);

  const state = user.onboardingState;

  let step;
  if (localOnboarding?.step === BASE_ONBOARDING.SKIPPED.name) {
    step = BASE_ONBOARDING.SKIPPED;
  } else if (state) {
    step = BASE_ONBOARDING[state];
  }

  await init({ step, userId: user.id });
};

/**
 * Provides an optimistic check for user login state using the profile token
 *
 * Used for initial UI rendering to prevent layout "pop-in" before the full auth state is confirmed.
 * Uses the existing auth token infrastructure rather than maintaining a separate flag.
 *
 * Note: This is NOT a security check - it's only used for optimistic UI rendering
 * The actual auth state is still determined by the user store.
 *
 * @returns {boolean} True if a profile token exists, suggesting user might be logged in
 */
export const isUserLoggedInOptimistic = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return !!getProfileToken();
};
